import React, { useMemo } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	Link,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
	FOUNDER_BILLING_CONTACT,
	formatBillingDate,
	SUBSCRIPTION_STATUS_COLORS,
	SUBSCRIPTION_STATUS_LABELS,
} from '../constants';
import { SectionCard, SectionHeader } from '../components/SettingsShell';
import TrialBillingSection from './TrialBillingSection';
import { EXTRA_SEAT_MONTHLY, PLAN_PRICE, PRO_INCLUDED_SEATS } from '../../../Shared/pricing';

const BillingSection = ({
	subscription,
	subscriptionLoading,
	subscriptionStatus,
	subscriptionExempt,
	organizationName,
	currentPlanTier,
	openingBillingPortal,
	reactivating,
	cancelingScheduledDowngrade,
	onManageBilling,
	onUpgradeToPro,
	onPrepareDowngrade,
	onCancelSubscription,
	onReactivateSubscription,
	onCancelScheduledDowngrade,
	trialActive = false,
	trialEndsAt,
	onChoosePlan,
}) => {
	const statusLabel =
		SUBSCRIPTION_STATUS_LABELS[subscriptionStatus] || subscriptionStatus;
	const statusColor =
		SUBSCRIPTION_STATUS_COLORS[subscriptionStatus] || 'default';
	const trialEndLabel = formatBillingDate(subscription?.trial_end);
	const periodEndLabel = formatBillingDate(subscription?.current_period_end);
	const seats = subscription?.seats;
	const isPro = subscription?.plan === 'pro';
	const isBasic = subscription?.plan === 'basic';
	const proBase = subscription?.pro_base_monthly_usd ?? PLAN_PRICE.pro;
	const extraSeat =
		subscription?.extra_seat_monthly_usd ?? EXTRA_SEAT_MONTHLY;
	const includedSeats = seats?.included_seats ?? PRO_INCLUDED_SEATS;
	const heldInactive = seats?.held_inactive_count > 0;
	const scheduledDowngrade = subscription?.scheduled_plan === 'basic' && isPro;
	const scheduledDowngradeLabel = formatBillingDate(
		subscription?.scheduled_plan_change_at,
	);
	const canCancel =
		!subscription?.cancel_at_period_end &&
		!scheduledDowngrade &&
		subscription?.status !== 'canceled';

	const cancellationMailto = useMemo(() => {
		const org = organizationName?.trim() || 'My organization';
		const subject = encodeURIComponent(`Account cancellation request — ${org}`);
		const body = encodeURIComponent(
			`Hi Evan,\n\nI'd like to cancel our Bidzbase account for ${org}.\n\nPlease let me know the next steps.\n\nThanks,`,
		);
		return `mailto:${FOUNDER_BILLING_CONTACT.email}?subject=${subject}&body=${body}`;
	}, [organizationName]);

	const planChangeMailto = useMemo(() => {
		const org = organizationName?.trim() || 'My organization';
		const subject = encodeURIComponent(`Subscription change — ${org}`);
		const body = encodeURIComponent(
			`Hi Evan,\n\nI'd like to discuss a subscription change for ${org}.\n\n`,
		);
		return `mailto:${FOUNDER_BILLING_CONTACT.email}?subject=${subject}&body=${body}`;
	}, [organizationName]);

	if (trialActive) {
		return (
			<TrialBillingSection
				subscriptionLoading={subscriptionLoading}
				trialEndsAt={
					subscription?.trial_ends_at || subscription?.trial_end || trialEndsAt
				}
				onChoosePlan={onChoosePlan}
			/>
		);
	}

	if (subscriptionExempt) {
		return (
			<SectionCard id="billing">
				<SectionHeader
					icon={CreditCardOutlinedIcon}
					title="Subscription & billing"
					description="Your organization is on a special arrangement with Bidzbase."
					titleAdornment={
						subscriptionLoading ? null : (
							<Chip
								label="Special arrangement"
								size="small"
								color="success"
								variant="outlined"
							/>
						)
					}
				/>

				{subscriptionLoading ? (
					<Stack spacing={2}>
						<Skeleton variant="text" width="80%" />
						<Skeleton variant="rounded" height={72} />
					</Stack>
				) : (
					<Stack spacing={2.5}>
						<Alert severity="success">
							Your organization is on a <strong>special deal</strong> with
							Bidzbase — you have full product access without standard
							self-serve subscription billing.
						</Alert>

						<Stack spacing={0.75}>
							<Typography variant="body1" fontWeight={600}>
								{subscription?.plan_name || currentPlanTier?.name || 'Plan'}{' '}
								plan
							</Typography>
							{isPro && seats ? (
								<Typography variant="body2" color="text.secondary">
									{seats.active_seat_count} active seat
									{seats.active_seat_count === 1 ? '' : 's'} in use
								</Typography>
							) : null}
							{currentPlanTier?.tagline ? (
								<Typography variant="body2" color="text.secondary">
									{currentPlanTier.tagline}
								</Typography>
							) : null}
						</Stack>

						<Alert severity="info">
							To change your plan, adjust seats, or discuss pricing, call or
							email <strong>{FOUNDER_BILLING_CONTACT.name}</strong> directly —
							friend and founding accounts aren&apos;t managed through the
							Stripe billing portal.
						</Alert>

						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
							<Button
								variant="contained"
								startIcon={<EmailOutlinedIcon />}
								component="a"
								href={planChangeMailto}
								sx={{ width: { xs: '100%', sm: 'auto' } }}
							>
								Email {FOUNDER_BILLING_CONTACT.name}
							</Button>
						</Stack>

						<Box
							sx={{
								pt: 1,
								borderTop: '1px solid',
								borderColor: 'divider',
							}}
						>
							<Typography variant="subtitle2" gutterBottom>
								Close your account
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 1.5 }}
							>
								You can request cancellation at any time. We&apos;ll confirm by
								email and retain your data for 30 days in case you change your
								mind.
							</Typography>
							<Button
								variant="outlined"
								color="warning"
								component="a"
								href={cancellationMailto}
								sx={{ width: { xs: '100%', sm: 'auto' } }}
							>
								Request account cancellation
							</Button>
						</Box>
					</Stack>
				)}
			</SectionCard>
		);
	}

	return (
		<SectionCard id="billing">
			<SectionHeader
				icon={CreditCardOutlinedIcon}
				title="Subscription & billing"
				description="View your plan, update payment methods, and manage upgrades or cancellation."
				titleAdornment={
					subscriptionLoading ? null : (
						<Stack direction="row" spacing={1} flexWrap="wrap">
							<Chip
								label={
									subscription?.plan_name || currentPlanTier?.name || 'Plan'
								}
								size="small"
								color="primary"
								variant="outlined"
							/>
							<Chip
								label={statusLabel}
								size="small"
								color={statusColor}
								variant="outlined"
							/>
						</Stack>
					)
				}
			/>

			{subscriptionLoading ? (
				<Stack spacing={2}>
					<Skeleton variant="text" width="60%" />
					<Skeleton variant="rounded" height={40} width={180} />
				</Stack>
			) : (
				<Stack spacing={2.5}>
					{subscription?.status === 'past_due' ? (
						<Alert severity="warning">
							Your last payment failed. Update your payment method in the
							billing portal to keep access.
						</Alert>
					) : null}

					{scheduledDowngrade ? (
						<Alert severity="info">
							Your plan will change to Basic
							{scheduledDowngradeLabel
								? ` on ${scheduledDowngradeLabel}`
								: ' at the end of your billing period'}
							. You keep full Pro access — including all team accounts — until
							then.
							<Box sx={{ mt: 1.5 }}>
								<Button
									variant="outlined"
									size="small"
									onClick={onCancelScheduledDowngrade}
									disabled={cancelingScheduledDowngrade}
								>
									{cancelingScheduledDowngrade
										? 'Canceling...'
										: 'Cancel scheduled downgrade'}
								</Button>
							</Box>
						</Alert>
					) : null}

					{subscription?.cancel_at_period_end ? (
						<Alert severity="warning">
							Your subscription is set to cancel
							{periodEndLabel
								? ` on ${periodEndLabel}`
								: ' at the end of the billing period'}
							. You keep access until then. Data is retained for 30 days after
							cancellation.
							<Box sx={{ mt: 1.5 }}>
								<Button
									variant="outlined"
									size="small"
									onClick={onReactivateSubscription}
									disabled={reactivating}
								>
									{reactivating ? 'Reactivating...' : 'Keep subscription'}
								</Button>
							</Box>
						</Alert>
					) : null}

					{subscription?.status === 'canceled' ? (
						<Alert severity="info">
							Your subscription is canceled. Choose a plan to reactivate, or
							contact support if you need help.
						</Alert>
					) : null}

					{heldInactive && isBasic ? (
						<Alert severity="info">
							You have {seats.held_inactive_count} deactivated account
							{seats.held_inactive_count === 1 ? '' : 's'} from a prior Pro
							plan. Upgrade to Pro to choose which accounts to restore.
						</Alert>
					) : null}

					<Stack spacing={0.75}>
						{subscription?.prepaid_commit_active &&
						subscription?.prepaid_commit_total_usd != null ? (
							<Typography variant="body1" fontWeight={600}>
								${subscription.prepaid_commit_total_usd} for 3 months
								{subscription?.price_monthly != null
									? ` · then $${subscription.price_monthly}/mo`
									: ''}
							</Typography>
						) : subscription?.price_monthly != null ? (
							<Typography variant="body1" fontWeight={600}>
								${subscription.price_monthly}/month
							</Typography>
						) : null}
						{isPro && seats ? (
							<Typography variant="body2" color="text.secondary">
								${proBase}/mo base (up to {includedSeats} seats)
								{seats.prepaid_unlimited_seats
									? ` · Seats above ${includedSeats} are unlimited for your 3-month prepaid term, then billed at $${extraSeat}/mo once monthly billing begins${
											seats.future_billable_extra_seats > 0
												? ` (${seats.future_billable_extra_seats} future extra seat${
														seats.future_billable_extra_seats === 1 ? '' : 's'
													}).`
												: '.'
										}`
									: seats.billable_extra_seats > 0
										? ` + ${seats.billable_extra_seats} extra seat${
												seats.billable_extra_seats === 1 ? '' : 's'
											} × $${extraSeat}/mo`
										: null}
							</Typography>
						) : null}
						{isPro && seats ? (
							<Typography variant="body2" color="text.secondary">
								{seats.active_seat_count} active seat
								{seats.active_seat_count === 1 ? '' : 's'} in use
							</Typography>
						) : null}
						{subscriptionStatus === 'trialing' && trialEndLabel ? (
							<Typography variant="body2" color="text.secondary">
								Trial ends {trialEndLabel}
							</Typography>
						) : null}
						{subscriptionStatus !== 'trialing' &&
						periodEndLabel &&
						subscription?.status !== 'canceled' ? (
							<Typography variant="body2" color="text.secondary">
								{subscription?.cancel_at_period_end
									? `Access until ${periodEndLabel}`
									: `Next billing date: ${periodEndLabel}`}
							</Typography>
						) : null}
						{currentPlanTier?.tagline ? (
							<Typography variant="body2" color="text.secondary">
								{currentPlanTier.tagline}
							</Typography>
						) : null}
					</Stack>

					<Typography variant="body2" color="text.secondary">
						Compare plans on our{' '}
						<Link href="/pricing" target="_blank" rel="noopener">
							pricing page
						</Link>
						. Upgrades take effect immediately with prorated billing; downgrades
						apply at the end of your current period.
						{subscription?.prepaid_commit_active && isBasic
							? ' If you upgrade to Pro during your 3-month prepaid term, your subscription converts to monthly immediately and the remaining prepaid time is forfeited.'
							: null}
					</Typography>

					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
						{isBasic ? (
							<Button
								variant="contained"
								onClick={onUpgradeToPro}
								disabled={
									openingBillingPortal || !subscription?.billing_configured
								}
								sx={{ width: { xs: '100%', sm: 'auto' } }}
							>
								Upgrade to Pro
							</Button>
						) : null}
						{isPro ? (
							<Button
								variant="outlined"
								color="warning"
								onClick={onPrepareDowngrade}
								disabled={
									!subscription?.billing_configured || scheduledDowngrade
								}
								sx={{ width: { xs: '100%', sm: 'auto' } }}
							>
								{scheduledDowngrade
									? 'Downgrade scheduled'
									: 'Prepare downgrade to Basic'}
							</Button>
						) : null}
						<Button
							variant={isBasic || isPro ? 'outlined' : 'contained'}
							endIcon={<OpenInNewIcon />}
							onClick={onManageBilling}
							disabled={openingBillingPortal || !subscription?.portal_available}
							sx={{ width: { xs: '100%', sm: 'auto' } }}
						>
							{openingBillingPortal ? 'Opening...' : 'Update payment method'}
						</Button>
						{canCancel ? (
							<Button
								variant="text"
								color="warning"
								onClick={onCancelSubscription}
								disabled={!subscription?.billing_configured}
								sx={{ width: { xs: '100%', sm: 'auto' } }}
							>
								Cancel subscription
							</Button>
						) : null}
					</Stack>

					{!subscription?.portal_available ? (
						<Typography
							variant="caption"
							color="text.secondary"
							display="block"
						>
							Billing portal is unavailable for this account.
						</Typography>
					) : null}
				</Stack>
			)}
		</SectionCard>
	);
};

export default BillingSection;
